import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Dialog, IconButton } from '@mui/material';
import { Close as CloseIcon, NavigateBefore, NavigateNext } from '@mui/icons-material';
import AuthenticatedImage from './AuthenticatedImage';
import AuthenticatedVideo from './AuthenticatedVideo';

/* ------------------------------------------------------------------ */
/*  Helpers: decode base64 markers used by the RichTextEditor         */
/* ------------------------------------------------------------------ */
const decodeMarkerData = (b64) => {
    try {
        return JSON.parse(decodeURIComponent(escape(atob(b64))));
    } catch { return null; }
};
const escapeHtml = (str) => (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/* Render a TABLE marker as a proper HTML table */
const renderTableHtml = (data) => {
    if (!data || !data.headers) return '';
    const hCells = data.headers.map(h =>
        `<th style="border:1px solid #d0d7de;padding:8px 12px;background:#f6f8fa;text-align:left;font-weight:600;font-size:13px;">${escapeHtml(h)}</th>`
    ).join('');
    const bRows = (data.cells || []).map(row => {
        const tds = row.map(cell =>
            `<td style="border:1px solid #d0d7de;padding:8px 12px;font-size:13px;">${escapeHtml(cell)}</td>`
        ).join('');
        return `<tr>${tds}</tr>`;
    }).join('');
    return `<table style="border-collapse:collapse;width:100%;margin:8px 0;border:1px solid #d0d7de;border-radius:6px;overflow:hidden;"><thead><tr>${hCells}</tr></thead><tbody>${bRows}</tbody></table>`;
};

/* Render a CODE marker as a styled pre/code block */
const renderCodeHtml = (data) => {
    if (!data) return '';
    const code = escapeHtml(data.code || '');
    const lang = data.language || 'plaintext';
    const langLabel = lang.charAt(0).toUpperCase() + lang.slice(1);
    return `<div style="margin:8px 0;border:1px solid #e1e4e8;border-radius:6px;overflow:hidden;">` +
        `<div style="display:flex;align-items:center;padding:4px 12px;background:#f6f8fa;border-bottom:1px solid #e1e4e8;font-size:11px;color:#656d76;font-family:monospace;">${langLabel}</div>` +
        `<pre style="margin:0;padding:12px 16px;background:#ffffff;color:#24292f;font-family:'Fira Code','Consolas','Monaco',monospace;font-size:13px;line-height:1.6;overflow-x:auto;white-space:pre-wrap;"><code>${code}</code></pre>` +
        `</div>`;
};

/**
 * IssueContentRenderer Component
 * 
 * Renders issue description HTML content with inline media placeholders replaced
 * by actual media components (images/videos from Confluence).
 * 
 * This component takes HTML content with placeholders like:
 * [IMAGE: filename.png] or [VIDEO: recording.mp4]
 * 
 * And replaces them with actual media components that fetch from Confluence.
 * 
 * @param {Object} props
 * @param {string} props.htmlContent - The HTML content with placeholders
 * @param {Array} props.mediaUrls - Array of media URLs from Confluence (screenshots and video)
 * @param {string} props.videoUrl - Video URL from issue
 * @param {string} props.screenshotUrls - Newline-separated screenshot URLs from issue
 */
const IssueContentRenderer = ({ htmlContent = '', mediaUrls = [], videoUrl = '', screenshotUrls = '' }) => {
    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // Parse video URLs (support multiple videos as newline-separated)
    const videos = useMemo(() => {
        if (videoUrl && typeof videoUrl === 'string') {
            return videoUrl.split('\n').filter(url => url.trim() !== '');
        }
        return [];
    }, [videoUrl]);

    // Parse screenshot URLs
    const screenshots = useMemo(() => {
        if (screenshotUrls && typeof screenshotUrls === 'string') {
            return screenshotUrls.split('\n').filter(url => url.trim() !== '');
        }
        return [];
    }, [screenshotUrls]);

    // Combine all media - videos first, then screenshots
    const allMedia = useMemo(() => {
        const media = [];
        
        // Add all videos
        videos.forEach(url => {
            media.push({ type: 'video', url });
        });
        
        // Add screenshots
        screenshots.forEach(url => {
            media.push({ type: 'image', url });
        });
        
        // DEBUG: Log media array
        console.log('[IssueContentRenderer] allMedia:', { 
            videoCount: videos.length, 
            screenshotCount: screenshots.length, 
            totalMedia: media.length 
        });
        
        return media;
    }, [videos, screenshots]);

    // Collect only image URLs for the lightbox gallery
    const lightboxImages = useMemo(() => allMedia.filter(m => m.type === 'image'), [allMedia]);

    const openLightbox = (url) => {
        const idx = lightboxImages.findIndex(m => m.url === url);
        setLightboxIndex(idx >= 0 ? idx : 0);
        setLightboxOpen(true);
    };

    // Parse HTML and replace placeholders with media components
    const renderContent = () => {
        if (!htmlContent || htmlContent.trim() === '' || htmlContent === '<p></p>\n') {
            // If no description but has media, show them
            if (allMedia.length > 0) {
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            No description provided.
                        </Typography>
                    </Box>
                );
            }
            return <Typography variant="body2" color="text.secondary">No description provided.</Typography>;
        }

        // Split content by media placeholders AND table/code markers
        const placeholderRegex = /\[(IMAGE|VIDEO):\s*([^\]]+)\]|\[(TABLE|CODE):([A-Za-z0-9+/=]+)\]/g;
        const parts = [];
        let lastIndex = 0;
        // Use separate counters for each media type to match placeholders correctly
        let videoIndex = 0;
        let imageIndex = 0;
        let match;

        while ((match = placeholderRegex.exec(htmlContent)) !== null) {
            // Add text before placeholder
            if (match.index > lastIndex) {
                const textContent = htmlContent.substring(lastIndex, match.index);
                if (textContent.trim()) {
                    parts.push({
                        type: 'html',
                        content: textContent,
                        key: `text-${lastIndex}`
                    });
                }
            }

            if (match[1]) {
                // IMAGE or VIDEO marker - match with corresponding media array
                const mediaType = match[1].toLowerCase();
                const fileName = match[2].trim();
                
                if (mediaType === 'video' && videoIndex < videos.length) {
                    parts.push({
                        type: 'video',
                        url: videos[videoIndex],
                        fileName: fileName,
                        key: `video-${videoIndex}`
                    });
                    videoIndex++;
                } else if (mediaType === 'image' && imageIndex < screenshots.length) {
                    parts.push({
                        type: 'image',
                        url: screenshots[imageIndex],
                        fileName: fileName,
                        key: `image-${imageIndex}`
                    });
                    imageIndex++;
                } else {
                    parts.push({
                        type: 'placeholder',
                        content: match[0],
                        key: `placeholder-${match.index}`
                    });
                }
            } else if (match[3] === 'TABLE') {
                // TABLE marker → decode and render as HTML table
                const data = decodeMarkerData(match[4]);
                if (data) {
                    parts.push({
                        type: 'html',
                        content: renderTableHtml(data),
                        key: `table-${match.index}`
                    });
                }
            } else if (match[3] === 'CODE') {
                // CODE marker → decode and render as styled code block
                const data = decodeMarkerData(match[4]);
                if (data) {
                    parts.push({
                        type: 'html',
                        content: renderCodeHtml(data),
                        key: `code-${match.index}`
                    });
                }
            }

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text after last placeholder
        if (lastIndex < htmlContent.length) {
            const textContent = htmlContent.substring(lastIndex);
            if (textContent.trim()) {
                parts.push({
                    type: 'html',
                    content: textContent,
                    key: `text-${lastIndex}`
                });
            }
        }

        // If no placeholders found, render the entire HTML
        if (parts.length === 0) {
            parts.push({
                type: 'html',
                content: htmlContent,
                key: 'full-content'
            });
        }

        return parts.map(part => {
            switch (part.type) {
                case 'html':
                    return (
                        <Box 
                            key={part.key}
                            dangerouslySetInnerHTML={{ __html: part.content }}
                            sx={{ 
                                '& p': { margin: '0.5em 0' },
                                '& ul, & ol': { marginLeft: '1.5em' },
                                '& h1, & h2, & h3, & h4, & h5, & h6': { marginTop: '0.75em', marginBottom: '0.5em' },
                                '& blockquote': { borderLeft: '3px solid #ddd', paddingLeft: '1em', margin: '1em 0', color: '#666' },
                                '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '0.9em' },
                                '& pre code': { backgroundColor: 'transparent', padding: 0 },
                                '& table': { borderCollapse: 'collapse', width: '100%', margin: '8px 0' },
                                '& th, & td': { border: '1px solid #d0d7de', padding: '8px 12px', fontSize: '13px' },
                                '& th': { background: '#f6f8fa', fontWeight: 600 },
                            }}
                        />
                    );
                case 'image':
                    return (
                        <Paper 
                            key={part.key}
                            elevation={2}
                            sx={{ 
                                my: 2, 
                                p: 1, 
                                display: 'inline-block',
                                maxWidth: '100%',
                                cursor: 'pointer',
                                '&:hover': { boxShadow: 6 }
                            }}
                            onClick={() => openLightbox(part.url)}
                        >
                            <AuthenticatedImage 
                                src={part.url} 
                                alt={part.fileName}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '400px',
                                    display: 'block',
                                    borderRadius: '4px'
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {part.fileName} — Click to enlarge
                            </Typography>
                        </Paper>
                    );
                case 'video':
                    return (
                        <Paper 
                            key={part.key}
                            elevation={2}
                            sx={{ 
                                my: 2, 
                                p: 1,
                                maxWidth: '100%'
                            }}
                        >
                            <AuthenticatedVideo 
                                src={part.url}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '400px',
                                    display: 'block',
                                    borderRadius: '4px'
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {part.fileName}
                            </Typography>
                        </Paper>
                    );
                case 'placeholder':
                    return (
                        <Box 
                            key={part.key}
                            sx={{ 
                                my: 1, 
                                p: 1, 
                                bgcolor: '#f5f5f5', 
                                borderRadius: 1,
                                display: 'inline-block'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {part.content}
                            </Typography>
                        </Box>
                    );
                default:
                    return null;
            }
        });
    };

    return (
        <Box sx={{ py: 1 }}>
            {renderContent()}
            
            {/* Fallback: If there are media files but no placeholders in HTML, show them at the end */}
            {allMedia.length > 0 && (!htmlContent || !htmlContent.match(/\[(IMAGE|VIDEO):/g)) && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        📎 Attachments ({allMedia.length})
                    </Typography>
                    {allMedia.map((media, index) => (
                        <Paper 
                            key={`fallback-${index}`}
                            elevation={2}
                            sx={{ 
                                my: 1, 
                                p: 1,
                                maxWidth: '100%',
                                ...(media.type === 'image' && { cursor: 'pointer', '&:hover': { boxShadow: 6 } })
                            }}
                            onClick={media.type === 'image' ? () => openLightbox(media.url) : undefined}
                        >
                            {media.type === 'image' ? (
                                <AuthenticatedImage 
                                    src={media.url}
                                    alt={`Attachment ${index + 1}`}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        display: 'block',
                                        borderRadius: '4px'
                                    }}
                                />
                            ) : (
                                <AuthenticatedVideo 
                                    src={media.url}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        display: 'block',
                                        borderRadius: '4px'
                                    }}
                                />
                            )}
                        </Paper>
                    ))}
                </Box>
            )}

            {/* ====== Lightbox Dialog ====== */}
            <Dialog
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(0,0,0,0.92)',
                        boxShadow: 'none',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        m: 1,
                        overflow: 'hidden',
                        borderRadius: 2,
                    }
                }}
            >
                {lightboxImages.length > 0 && (
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                        {/* Close button */}
                        <IconButton
                            onClick={() => setLightboxOpen(false)}
                            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, zIndex: 2 }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {/* Previous */}
                        {lightboxImages.length > 1 && (
                            <IconButton
                                onClick={() => setLightboxIndex((lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length)}
                                sx={{ position: 'absolute', left: 8, color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, zIndex: 2 }}
                            >
                                <NavigateBefore />
                            </IconButton>
                        )}

                        {/* Image */}
                        <AuthenticatedImage
                            src={lightboxImages[lightboxIndex]?.url}
                            alt={`Screenshot ${lightboxIndex + 1}`}
                            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 4 }}
                        />

                        {/* Next */}
                        {lightboxImages.length > 1 && (
                            <IconButton
                                onClick={() => setLightboxIndex((lightboxIndex + 1) % lightboxImages.length)}
                                sx={{ position: 'absolute', right: 8, color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, zIndex: 2 }}
                            >
                                <NavigateNext />
                            </IconButton>
                        )}

                        {/* Counter */}
                        {lightboxImages.length > 1 && (
                            <Typography
                                variant="caption"
                                sx={{ position: 'absolute', bottom: 12, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', px: 2, py: 0.5, borderRadius: 1 }}
                            >
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </Typography>
                        )}
                    </Box>
                )}
            </Dialog>
        </Box>
    );
};

export default IssueContentRenderer;
